import { Alert } from '@bbpos/ui';
import { hourLabel } from '@bbpos/utils';

interface HourlyBucket {
  hour: number;
  bill_count: number;
  revenue: number;
}

interface StaffInsightBannerProps {
  /** Hourly sales for today (IST), as returned by /dashboard/sales-wave. */
  hourly: HourlyBucket[];
  totalBillsToday: number;
  dismissed: boolean;
  onDismiss: () => void;
}

/**
 * Owner-facing nudge: "you have a sustained peak, consider extra staff".
 *
 * Heuristic — only fires when ALL of these are true:
 *   1. At least 10 bills today (don't pester on tiny days).
 *   2. There is a contiguous window of ≥ 2 hours where each hour carries at
 *      least 15 % of the day's revenue.
 *   3. That window's total share is ≥ 40 % of the day.
 *
 * Otherwise stays silent. The previous version fired on a single peak hour
 * and arbitrarily added "+2 hours" to the label — telling a tenant with one
 * busy hour and 4 total bills to "add 2 staff" is noise.
 */
export function StaffInsightBanner({
  hourly,
  totalBillsToday,
  dismissed,
  onDismiss,
}: StaffInsightBannerProps) {
  if (dismissed) return null;

  // Cold start — no bills yet today, friendly hint.
  if (totalBillsToday === 0) {
    return (
      <Alert
        variant="info"
        title="Staff Insight"
        description="Start billing to unlock peak-hour insights. We'll highlight your busiest windows so you can staff accordingly."
        dismissible
        onDismiss={onDismiss}
      />
    );
  }

  const window = findPeakWindow(hourly);
  if (!window || totalBillsToday < 10) {
    return null;
  }

  const description =
    window.length === 1
      ? `Busiest hour today: ${hourLabel(window.startHour)} — ${window.bills} bills, ${window.sharePct}% of day's revenue.`
      : `Sustained peak: ${hourLabel(window.startHour)}–${hourLabel(window.endHour + 1)} carries ${window.sharePct}% of today's revenue. Consider rostering an extra cashier during this window.`;

  return (
    <Alert
      variant="info"
      title="Staff Insight"
      description={description}
      dismissible
      onDismiss={onDismiss}
    />
  );
}

interface PeakWindow {
  startHour: number;
  endHour: number;
  length: number;
  bills: number;
  sharePct: number;
}

/** Find the longest contiguous run of "hot" hours (≥15% of day each). */
function findPeakWindow(hourly: HourlyBucket[]): PeakWindow | null {
  if (hourly.length === 0) return null;
  const totalRevenue = hourly.reduce((s, h) => s + h.revenue, 0);
  if (totalRevenue === 0) return null;

  const HOT_THRESHOLD = 0.15;
  const WINDOW_THRESHOLD = 0.40;

  const sorted = [...hourly].sort((a, b) => a.hour - b.hour);

  let best: PeakWindow | null = null;
  let runStart = -1;
  let runRevenue = 0;
  let runBills = 0;

  const flush = (endHour: number) => {
    if (runStart === -1) return;
    const sharePct = Math.round((runRevenue / totalRevenue) * 100);
    const length = endHour - runStart + 1;
    if (length >= 2 && sharePct >= WINDOW_THRESHOLD * 100) {
      if (!best || length > best.length || (length === best.length && sharePct > best.sharePct)) {
        best = { startHour: runStart, endHour, length, bills: runBills, sharePct };
      }
    }
    runStart = -1;
    runRevenue = 0;
    runBills = 0;
  };

  for (let i = 0; i < sorted.length; i++) {
    const h = sorted[i];
    const share = h.revenue / totalRevenue;
    const contiguous = runStart !== -1 && sorted[i - 1]?.hour === h.hour - 1;
    if (share >= HOT_THRESHOLD) {
      if (!contiguous) {
        flush(sorted[i - 1]?.hour ?? h.hour);
        runStart = h.hour;
        runRevenue = h.revenue;
        runBills = h.bill_count;
      } else {
        runRevenue += h.revenue;
        runBills += h.bill_count;
      }
    } else if (runStart !== -1) {
      flush(sorted[i - 1].hour);
    }
  }
  flush(sorted[sorted.length - 1].hour);

  if (best) return best;

  // Fallback for single-hour cases: still surface the busiest hour when no
  // qualifying multi-hour window exists, but use length=1 so the wording
  // changes from "sustained peak" → "busiest hour".
  const top = [...sorted].sort((a, b) => b.revenue - a.revenue)[0];
  if (top && top.revenue / totalRevenue >= 0.5) {
    return {
      startHour: top.hour,
      endHour: top.hour,
      length: 1,
      bills: top.bill_count,
      sharePct: Math.round((top.revenue / totalRevenue) * 100),
    };
  }
  return null;
}
